import React, { useEffect } from 'react'
import { Switch, Route, NotFound, Redirect } from '@fs/zion-router'
import { Separator, useOverlay } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import { useFamilyGroupContext } from '../../providers/FamilyGroupProvider'
import { usePWAMode } from '../../hooks/appRootHooks'
import getIsTWAMode from '../../helpers/appMode_helper'
import {
  getTwaNotificationsEnabled,
  hasPromptedTwaNotifications,
  markTwaNotificationsPrompted,
} from '../../helpers/twaNotifications'
import useSeedNotificationDefaults from '../../hooks/useSeedNotificationDefaults'
import useRefreshNotificationStateOnResume from '../../hooks/useRefreshNotificationStateOnResume'
import { NOTIFICATIONS_FEATURE_FLAG } from '../../services/notificationPrefsService'
import AuthRouteWithFallback from '../../components/AuthRouteWithFallback'
import Story from '../Story'
import Temple from '../Temple'
import More from '../More'
import Notifications from '../Notifications'
import CreateFamilyGroupPrompt from '../../components/CreateFamilyGroupPrompt/CreateFamilyGroupPrompt'
import Activities from '../Activities'

const LandingPage = React.lazy(() => import('../Landing/LandingPage'))
const HomeFeedPage = React.lazy(() => import('./HomeFeedPage'))

export default function KidsHome() {
  const { userReadyAndShowFamilyGroupPrompt, loadFamilyGroup, setShowAddFamilyGroupPrompt } = useFamilyGroupContext()
  const overlay = useOverlay({ initiallyOpen: true })
  const isPWAMode = usePWAMode()
  const { signedIn } = useUser()
  const notificationsFeatureEnabled = useFeatureFlag(NOTIFICATIONS_FEATURE_FLAG).isOn

  // Seed default push subscriptions once the user first allows notifications
  useSeedNotificationDefaults()
  // Re-sync the OS notification permission whenever the app returns to the foreground
  useRefreshNotificationStateOnResume()

  useEffect(() => {
    // Ask for push-notification permission after the user is signed in.
    if (!signedIn) return

    if (getIsTWAMode()) {
      // The TWA notifications feature is gated (the NPS endpoints aren't on prod yet), so skip the native
      // permission prompt when it's off. The PWA web-push prompt below is the pre-existing behavior and is
      // intentionally left ungated.
      if (!notificationsFeatureEnabled) return
      // Inside the Android TWA, Chrome's delegated web prompt does not reliably surface, so trigger the
      // native POST_NOTIFICATIONS request via the app's custom scheme handler (UrlHandlerActivity).
      // Notification.permission is unreliable here (it never leaves 'default'), so gate on the
      // native-reported state instead: skip when notifications are already enabled, and prompt at most
      // once per session — otherwise this fires on every load/relaunch and destabilizes the TWA task.
      if (getTwaNotificationsEnabled() === true || hasPromptedTwaNotifications()) return
      markTwaNotificationsPrompted()
      window.location.href = 'togetherscheme://request-notifications'
    } else if (isPWAMode && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [signedIn, isPWAMode, notificationsFeatureEnabled])

  return (
    <>
      {userReadyAndShowFamilyGroupPrompt && (
        <CreateFamilyGroupPrompt
          overlay={overlay}
          loadFamilyGroup={loadFamilyGroup}
          onOverlayClose={() => setShowAddFamilyGroupPrompt(false)}
        />
      )}
      <Switch>
        <Redirect from="/story" to="/stories" />

        <AuthRouteWithFallback path="/stories" exact component={Story} fallback={LandingPage} />
        <AuthRouteWithFallback path="/feed" exact component={HomeFeedPage} fallback={LandingPage} />
        <AuthRouteWithFallback path="/temple" exact component={Temple} fallback={LandingPage} />
        <AuthRouteWithFallback path="/activities" exact component={Activities} fallback={LandingPage} />
        <AuthRouteWithFallback path="/more" exact component={More} fallback={LandingPage} />
        <AuthRouteWithFallback path="/notifications" exact component={Notifications} fallback={LandingPage} />
        <Route component={NotFound} />
      </Switch>
      {isPWAMode && <Separator size="xxl" />}
    </>
  )
}
